import { Stack } from '@mui/system'
import { Button, Typography, ButtonGroup, Select, MenuItem, FormControl, InputLabel } from '@mui/material'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import createDialogTitle from '../generic/utilities/dialogUtil'
import { Sort } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import ArrangeDevices from '../routes/ArrangeDevices'
import { apiPut, apiGet } from '../generic/Api_Functions'
import usePlannerAuthorization from '../generic/hooks/useAuthorization/usePlannerAuthorization'
import useDateFormatter from '../generic/hooks/useDateFormatter/useDateFormatter'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import i18n from '../../i18n'

const PlannerCalendarToolbar = (props) => {
  const {
    selectedFile,
    setDatesInfo,
    onDateChange,
    currentDuration,
    dispatchNewDuration,
    setGroupby,
    groupby,
    plannerDevices,
    setPlannerDevices,
    showAlert,
    unitCriteriaName
  } = props
  const { t_ } = useSmartTranslation()
  const [showArrangeDialog, setShowArrangeDialog] = useState(false)
  const [arrangeDevices, setArrangeDevices] = useState([])
  const [footPrintDialogOpen, setFootPrintDialogOpen] = useState(false)
  const [footPrintColumns, setFootPrintColumns] = useState([])
  const [headerGroups, setHeaderGroups] = useState([])
  const isPlannerUserAuthorized = usePlannerAuthorization()
  const { formatDate } = useDateFormatter()
  const currentDate = new Date()
  const disableTodayButton = setDatesInfo === null || (setDatesInfo.start < currentDate && setDatesInfo.end > currentDate)

  const changeView = async (newView) => {
    // Dont allow deselecing view
    if (newView == null) {
      return
    }
    dispatchNewDuration({ type: 'SET_VIEW', mode: newView })
  }

  // Check currently selected view
  let currentView = null
  if (currentDuration !== null) {
    currentView = currentDuration?.mode
  }
  const handleModeChange = (event) => {
    setGroupby(event.target.value)
  }

  useEffect(() => {
    setArrangeDevices(plannerDevices)
  }, [plannerDevices])

  const setVisibility = (device) => {
    arrangeDevices.forEach(element => {
      if (element.id === device) {
        element.visible = !element.visible
      }
    })
    setArrangeDevices([...arrangeDevices])
  }
  const handleSortingChange = (sortedParameters) => {
    // Sorting change has been performed
    const reordered = sortedParameters.map((element, index) => ({
      ...element,
      orderIndex: index + 1
    }))
    setArrangeDevices(reordered)
  }
  const saveSorting = async () => {
    await apiPut('devicesPlannerSummary/update', arrangeDevices)
      .then(() => {
        setPlannerDevices(arrangeDevices)
        return showAlert({
          severity: 'success',
          title: t_('Update successful'),
          message: t_('The operation was correctly performed')
        })
      })
      .catch(error => {
        return showAlert(
          {
            title: t_('Update error'),
            severity: error.name,
            message: t_(error.message)
          }
        )
      })
    setShowArrangeDialog(false)
  }

  useEffect(() => {
    if (unitCriteriaName !== 'Job' && groupby === 'parts') {
      setGroupby('devices')
    }
  }, [unitCriteriaName, groupby, setGroupby])

  const dataFetch = async () => {
    const response = await apiGet('footPrint/byPlanification/' + selectedFile.id)
    if (!response?.length) return []

    const monthsRaw = Array.from(new Set((response || []).map(item => (item.yearMonth || '').replace(/\s+/g, ''))))
    const monthsSorted = monthsRaw.sort((a, b) => a.localeCompare(b))

    const secondsToHHMM = (seconds) => {
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      return `${h}:${m < 10 ? '0' : ''}${m}`
    }

    const sanitizeMonthKey = (yearMonth) => {
      const ym = yearMonth.replace(/\s+/g, '')
      const [year, month] = ym.split('-')
      return `y${year}m${month}`
    }

    const monthLabel = (yearMonth) => {
      const ym = yearMonth.replace(/\s+/g, '')
      const [year, month] = ym.split('-')
      const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1)
      const locale = i18n?.language || 'en'
      const name = date.toLocaleDateString(locale, { month: 'long' })
      return `${name.toUpperCase()}-${year}`
    }

    const dynamicColumns = [{ name: t_('Asset'), field: 'deviceName' }]

    monthsSorted.forEach((ym) => {
      const key = sanitizeMonthKey(ym)
      dynamicColumns.push({
        name: t_('Planned hours'),
        field: `months.${key}.plannedHours`,
        render: (row) => {
          const v = row?.months?.[key]?.plannedHours
          return secondsToHHMM(v || 0)
        }
      })
      dynamicColumns.push({ name: `${t_('Consumed energy')} (kWh)`, field: `months.${key}.energyConsumed` })
      dynamicColumns.push({ name: `${t_('CO2 Emission')} (kgCO₂e)`, field: `months.${key}.co2Emission` })
    })

    const groups = monthsSorted.map((ym, idx) => ({
      label: monthLabel(ym),
      startIndex: 1 + (idx * 3),
      colSpan: 3
    }))

    const devices = Array.from(new Set((response || []).map(item => item.deviceName)))
    const rows = devices.map((deviceName) => {
      const row = { deviceName, months: {} }
      monthsSorted.forEach((ym) => {
        const key = sanitizeMonthKey(ym)
        const rec = (response || []).find(r => r.deviceName === deviceName && (r.yearMonth || '').replace(/\s+/g, '') === ym)
        const seconds = rec ? Number(rec.secondsActive) || 0 : 0
        const energy = rec ? Math.round(Number(rec.energyConsumed) || 0) : 0
        const co2 = rec ? Math.round(Number(rec.co2Emissions) || 0) : 0
        row.months[key] = {
          plannedHours: seconds,
          energyConsumed: energy,
          co2Emission: co2
        }
      })
      return row
    })

    if (devices.length > 0) {
      const totalRow = { deviceName: t_('TOTAL MONTHLY'), months: {}, smartTotalRow: true }
      monthsSorted.forEach((ym) => {
        const key = sanitizeMonthKey(ym)
        const agg = rows.reduce((acc, r) => {
          const m = r.months[key] || { plannedHours: 0, energyConsumed: 0, co2Emission: 0 }
          acc.plannedHours += m.plannedHours
          acc.energyConsumed += m.energyConsumed
          acc.co2Emission += m.co2Emission
          return acc
        }, { plannedHours: 0, energyConsumed: 0, co2Emission: 0 })
        totalRow.months[key] = agg
      })
      rows.push(totalRow)
    }
    setFootPrintColumns(dynamicColumns)
    setHeaderGroups(groups)
    return rows
  }

  return (
    <>
      <SmartDialog
        setOpen={footPrintDialogOpen}
        cancel={false}
        accept={false}
        close
        closeCallback={() => setFootPrintDialogOpen(false)}
        renderComponent={
          <SmartMateriaUITable
            setOpen={setFootPrintDialogOpen}
            title={t_('Carbon Footprint')}
            columns={footPrintColumns}
            dataFetch={dataFetch}
            headerGroups={headerGroups}
          />
        }
      />
      <SmartDialog
        title={createDialogTitle({ title: t_('PLANNING ASSET ORDER'), icon: Sort })}
        setOpen={showArrangeDialog}
        renderComponent={<ArrangeDevices devices={arrangeDevices} setVisibility={setVisibility} sortingChange={handleSortingChange} itemId='orderIndex' />}
        accept
        cancel
        cancelCallback={() => { setShowArrangeDialog(false) }}
        acceptCallback={() => { saveSorting() }}
      />
      <Stack direction='row' alignItems='flex-end' justifyContent='space-between' flexWrap='wrap' rowGap={1.5} columnGap={3}>
        <Stack direction='row' gap={3}>
          <FormControl>
            <InputLabel>{t_('View')}</InputLabel>
            <Select label={t_('View')} value={groupby} onChange={(event) => handleModeChange(event)} size='small' sx={{ minWidth: '12rem' }}>
              <MenuItem value='devices' disabled={!isPlannerUserAuthorized('ASSETS_VIEW')}>{t_('Assets')}</MenuItem>
              <MenuItem value='parts' disabled={unitCriteriaName !== 'Job' || !isPlannerUserAuthorized('PARTS_VIEW')}>{t_('Parts')}</MenuItem>
              <MenuItem value='technologies' disabled={!isPlannerUserAuthorized('TECHNOLOGIES_VIEW')}>{t_('Technologies')}</MenuItem>
              <MenuItem value='references' disabled={!isPlannerUserAuthorized('REFERENCES_VIEW')}>{t_('References')}</MenuItem>
              <MenuItem value='fabrication_orders' disabled={!isPlannerUserAuthorized('FABRICATION_ORDERS_VIEW')}>{t_('Fabrication orders')}</MenuItem>
              <MenuItem value='workers' disabled={!isPlannerUserAuthorized('WORKERS_VIEW')}>{t_('Workers')}</MenuItem>
            </Select>
          </FormControl>

        </Stack>
        <Stack direction='row' columnGap={3} rowGap={1.5} alignItems='center' flexWrap='wrap'>

          <Typography noWrap variant='h4' sx={{ fontSize: '1.5rem', ml: 2 }}>{setDatesInfo !== null ? formatDate(setDatesInfo.start) + ' - ' + formatDate(setDatesInfo.end) : null}</Typography>
          <ButtonGroup>
            <Button variant='outlined' onClick={() => onDateChange('prev')}><KeyboardArrowLeftIcon /></Button>
            <Button variant='outlined' disabled={disableTodayButton} onClick={() => onDateChange('today')}>{t_('Today')}</Button>
            <Button variant='outlined' onClick={() => onDateChange('next')}><KeyboardArrowRightIcon /></Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button disabled={currentDuration.isMaxValue} onClick={() => dispatchNewDuration({ type: 'ZOOM_OUT' })}><ZoomOutIcon /></Button>
            <Button disabled={currentDuration.isMinValue} onClick={() => dispatchNewDuration({ type: 'ZOOM_IN' })}><ZoomInIcon /></Button>
          </ButtonGroup>
          <FormControl>
            <InputLabel>{t_('Period')}</InputLabel>
            <Select label={t_('Period')} value={currentView} onChange={(event) => changeView(event.target.value)} size='small' sx={{ minWidth: '7rem' }}>
              <MenuItem value='days'>{t_('Day')}</MenuItem>
              <MenuItem value='weeks'>{t_('Week')}</MenuItem>
              <MenuItem value='months'>{t_('Month')}</MenuItem>
              <MenuItem value='years'>{t_('Year')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            value='footprint'
            sx={{ border: '1px solid' }}
            onClick={() => setFootPrintDialogOpen(true)}
          >
            {t_('Carbon Footprint')}
          </Button>
          <Button value='arrange' sx={{ border: '1px solid' }} disabled={groupby !== 'devices' || !isPlannerUserAuthorized('PLANNING_ARRANGE')} onClick={() => setShowArrangeDialog(true)}>{t_('Arrange')}</Button>
        </Stack>
      </Stack>
    </>
  )
}

export default PlannerCalendarToolbar
